import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import '../styles/AdminPage.css';

export default function AdminPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [xmlFile, setXmlFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [dbMappings, setDbMappings] = useState(null);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbError, setDbError] = useState('');
  const [dbFilter, setDbFilter] = useState({ module: '', tag: '', questionName: '' });
  const [overrideDrafts, setOverrideDrafts] = useState({});
  const [overrideSavingId, setOverrideSavingId] = useState(null);
  const [unmappedFilter, setUnmappedFilter] = useState({ courseId: '', groupId: '', groupName: '' });
  const [unmappedData, setUnmappedData] = useState(null);
  const [unmappedLoading, setUnmappedLoading] = useState(false);
  const [unmappedError, setUnmappedError] = useState('');
  const [unmappedNotice, setUnmappedNotice] = useState('');
  const [unmappedOverrideDrafts, setUnmappedOverrideDrafts] = useState({});
  const [unmappedSavingKey, setUnmappedSavingKey] = useState('');
  const [unmappedBulkApplying, setUnmappedBulkApplying] = useState(false);

  const getUnmappedRowKey = (item, index) => (
    `${item.studentId}-${item.slot || 'no-slot'}-${item.questionId || index}`
  );

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const view = search.get('view');
    const courseId = search.get('courseId') || '';
    const groupId = search.get('groupId') || '';
    const groupName = search.get('groupName') || '';

    if (!courseId && !groupId && !groupName) {
      return;
    }

    setUnmappedFilter({ courseId, groupId, groupName });

    if (view === 'unmapped' && courseId) {
      handleLoadUnmappedItems(courseId, groupId);
    }
  }, [location.search]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setXmlFile(file || null);
    setFileName(file ? file.name : '');
    setPreviewData(null);
    setPreviewError('');
    setImportResult(null);
    setImportError('');
  };

  const handlePreview = async () => {
    if (!xmlFile) return;
    setPreviewLoading(true);
    setPreviewError('');
    setPreviewData(null);
    const formData = new FormData();
    formData.append('xml', xmlFile);
    try {
      const res = await apiClient.post('/api/admin/preview-xml-mapping', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreviewData(res.data.preview);
    } catch (err) {
      setPreviewError('Preview failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleImport = async () => {
    if (!xmlFile) return;
    setImportLoading(true);
    setImportError('');
    setImportResult(null);
    const formData = new FormData();
    formData.append('xml', xmlFile);
    try {
      const res = await apiClient.post('/api/admin/import-xml-mapping', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
    } catch (err) {
      setImportError('Import failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setImportLoading(false);
    }
  };

  const handleLoadDbMappings = async () => {
    setDbLoading(true);
    setDbError('');
    const params = {};
    if (dbFilter.module) params.module = dbFilter.module;
    if (dbFilter.tag) params.tag = dbFilter.tag;
    if (dbFilter.questionName) params.questionName = dbFilter.questionName;
    try {
      const res = await apiClient.get('/api/admin/db-mappings', { params });
      setDbMappings(res.data.mappings);
    } catch (err) {
      setDbError('Failed to load DB mappings: ' + (err.response?.data?.error || err.message));
    } finally {
      setDbLoading(false);
    }
  };

  const handleApplyOverride = async (row, objectiveNumber) => {
    const nextObjectiveNumber = String(objectiveNumber || '').trim();
    if (!nextObjectiveNumber) {
      return;
    }

    setOverrideSavingId(row.id);
    setDbError('');
    try {
      const res = await apiClient.post('/api/admin/question-objective-override', {
        questionId: row.id,
        objectiveNumber: nextObjectiveNumber,
        reason: row.suggestedObjectiveNumber === nextObjectiveNumber
          ? 'Applied suggested feedback objective from Admin page'
          : 'Applied manual objective override from Admin page'
      });

      setDbMappings((current) => (current || []).map((entry) => (
        entry.id === row.id ? res.data.mapping : entry
      )));
      setOverrideDrafts((current) => ({ ...current, [row.id]: nextObjectiveNumber }));
    } catch (err) {
      setDbError('Failed to apply override: ' + (err.response?.data?.error || err.message));
    } finally {
      setOverrideSavingId(null);
    }
  };

  const handleClearOverride = async (row) => {
    setOverrideSavingId(row.id);
    setDbError('');
    try {
      const res = await apiClient.post('/api/admin/question-objective-override', {
        questionId: row.id,
        objectiveNumber: '',
        reason: 'Cleared override from Admin page'
      });

      setDbMappings((current) => (current || []).map((entry) => (
        entry.id === row.id ? res.data.mapping : entry
      )));
      setOverrideDrafts((current) => ({ ...current, [row.id]: '' }));
    } catch (err) {
      setDbError('Failed to clear override: ' + (err.response?.data?.error || err.message));
    } finally {
      setOverrideSavingId(null);
    }
  };

  const handleLoadUnmappedItems = async (courseIdOverride, groupIdOverride) => {
    const courseId = String(courseIdOverride ?? unmappedFilter.courseId).trim();
    const groupId = String(groupIdOverride ?? unmappedFilter.groupId).trim();

    if (!courseId) {
      setUnmappedError('Course ID is required to load unmapped incorrect items.');
      return;
    }

    setUnmappedLoading(true);
    setUnmappedError('');
    setUnmappedNotice('');
    setUnmappedData(null);

    try {
      const response = await apiClient.get(`/api/reports/class/${courseId}/unmapped-items`, {
        params: groupId ? { groupId } : {}
      });
      setUnmappedData(response.data?.data || null);
      setUnmappedOverrideDrafts({});
    } catch (err) {
      setUnmappedError('Failed to load unmapped incorrect items: ' + (err.response?.data?.message || err.response?.data?.error || err.message));
    } finally {
      setUnmappedLoading(false);
    }
  };

  const handleApplyUnmappedOverride = async (item, rowKey) => {
    const objectiveNumber = String(unmappedOverrideDrafts[rowKey] || '').trim();

    if (!objectiveNumber) {
      setUnmappedError('Enter an objective number before applying override.');
      return;
    }

    if (!item.moduleNum || !item.slot) {
      setUnmappedError('Cannot apply override for this row because module or slot is missing.');
      return;
    }

    setUnmappedSavingKey(rowKey);
    setUnmappedError('');
    setUnmappedNotice('');

    try {
      await apiClient.post(`/api/reports/class/${unmappedFilter.courseId}/unmapped-items/override`, {
        moduleNum: item.moduleNum,
        slot: item.slot,
        objectiveNumber,
        reason: 'Assigned from Admin unmapped incorrect items table'
      });

      await handleLoadUnmappedItems(unmappedFilter.courseId, unmappedFilter.groupId);
    } catch (err) {
      setUnmappedError('Failed to apply unmapped override: ' + (err.response?.data?.error || err.message));
    } finally {
      setUnmappedSavingKey('');
    }
  };

  const handleApplyAllUnmappedOverrides = async () => {
    const items = Array.isArray(unmappedData?.items) ? unmappedData.items : [];
    if (items.length === 0) {
      setUnmappedError('No unmapped rows are available to update.');
      return;
    }

    const preparedRows = items.map((item, index) => {
      const rowKey = getUnmappedRowKey(item, index);
      const objectiveNumber = String(unmappedOverrideDrafts[rowKey] || '').trim();
      return { item, rowKey, objectiveNumber };
    });

    const rowsToApply = preparedRows.filter(({ item, objectiveNumber }) => (
      Boolean(objectiveNumber) && Boolean(item?.moduleNum) && Boolean(item?.slot)
    ));

    if (rowsToApply.length === 0) {
      setUnmappedError('Enter at least one objective number before using Apply All.');
      return;
    }

    setUnmappedBulkApplying(true);
    setUnmappedError('');
    setUnmappedNotice('');

    let successCount = 0;
    let failureCount = 0;
    const failedRows = [];

    for (const row of rowsToApply) {
      try {
        await apiClient.post(`/api/reports/class/${unmappedFilter.courseId}/unmapped-items/override`, {
          moduleNum: row.item.moduleNum,
          slot: row.item.slot,
          objectiveNumber: row.objectiveNumber,
          reason: 'Bulk assigned from Admin unmapped incorrect items table'
        });
        successCount += 1;
      } catch (err) {
        failureCount += 1;
        failedRows.push(`${row.item.studentName || 'Unknown student'} (slot ${row.item.slot || 'n/a'})`);
      }
    }

    await handleLoadUnmappedItems(unmappedFilter.courseId, unmappedFilter.groupId);

    if (failureCount > 0) {
      const sampleFailures = failedRows.slice(0, 3).join(', ');
      setUnmappedError(`Applied ${successCount} override(s); ${failureCount} failed. ${sampleFailures}`);
    } else {
      setUnmappedNotice(`Applied ${successCount} override(s) successfully.`);
    }

    setUnmappedBulkApplying(false);
  };

  return (
    <div className="admin-page">
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <h2 style={{ margin: 0 }}>Admin: XML Mapping &amp; Remediation Tools</h2>
        <div>
          <button onClick={() => navigate('/dashboard')} style={{ marginRight: 8, background: '#555' }}>&#8592; Dashboard</button>
          {onLogout && <button onClick={onLogout} style={{ background: '#c0392b' }}>Log Out</button>}
        </div>
      </header>

      {/* Upload */}
      <section style={{ marginBottom: 32 }}>
        <h3>Upload Moodle XML Export</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label className="file-label" style={{ display: 'inline-block', padding: '7px 16px', background: '#e8f0fe', border: '1px solid #1976d2', borderRadius: 4, cursor: 'pointer' }}>
            Choose XML File
            <input type="file" accept=".xml" onChange={handleFileChange} style={{ display: 'none' }} />
          </label>
          {fileName && <span style={{ fontStyle: 'italic', color: '#444' }}>{fileName}</span>}
          <button onClick={handlePreview} disabled={!xmlFile || previewLoading}>
            {previewLoading ? 'Previewing...' : 'Preview Mapping'}
          </button>
          <button onClick={handleImport} disabled={!xmlFile || importLoading} style={{ background: '#27ae60' }}>
            {importLoading ? 'Importing...' : 'Import to DB'}
          </button>
        </div>
      </section>

      {/* Preview Results */}
      <section style={{ marginBottom: 32 }}>
        <h3>Preview Results</h3>
        {previewError && <div className="status error">{previewError}</div>}
        {!previewData ? (
          <div className="status">No preview run yet. Choose an XML file and click Preview Mapping to inspect tags, objective matches, and warnings before import.</div>
        ) : (
          <>
            <div className="summary-cards">
              <div className="summary-card"><div className="card-value">{previewData.totalQuestions}</div><div className="card-label">Total Questions</div></div>
              <div className="summary-card"><div className="card-value">{previewData.questionsWithTags}</div><div className="card-label">With Tags</div></div>
              <div className="summary-card"><div className="card-value" style={{ color: previewData.questionsWithoutTags > 0 ? '#e67e22' : '#1976d2' }}>{previewData.questionsWithoutTags}</div><div className="card-label">Without Tags</div></div>
              <div className="summary-card"><div className="card-value" style={{ color: previewData.malformedRecords?.length > 0 ? '#c0392b' : '#1976d2' }}>{previewData.malformedRecords?.length || 0}</div><div className="card-label">Malformed</div></div>
              <div className="summary-card"><div className="card-value" style={{ color: previewData.warningCount > 0 ? '#e67e22' : '#1976d2' }}>{previewData.warningCount || 0}</div><div className="card-label">Warnings</div></div>
            </div>
            {previewData.duplicateNames?.length > 0 && (
              <div className="warning">⚠ Duplicate question names: {previewData.duplicateNames.join(', ')}</div>
            )}
            <h4>Preview Rows ({previewData.previewRows?.length})</h4>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Question Name</th>
                    <th>Tags</th>
                    <th>Objective Number</th>
                    <th>Objective Text</th>
                    <th>Feedback Snippet</th>
                    <th>Warning</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.previewRows?.map((row, i) => (
                    <tr key={i}>
                      <td>{row.questionName}</td>
                      <td>{(row.tags || []).join(', ')}</td>
                      <td>{row.objectiveNumber || <span style={{ color: '#aaa' }}>none</span>}</td>
                      <td style={{ minWidth: 320, fontWeight: 600 }}>{row.objectiveText || <span style={{ color: '#aaa' }}>unmapped</span>}</td>
                      <td style={{ fontSize: '0.78rem', color: '#666' }}>{row.generalFeedbackSnippet}</td>
                      <td style={{ fontSize: '0.78rem', color: row.warning ? '#c0392b' : '#888' }}>{row.warning || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* Import Results */}
      <section style={{ marginBottom: 32 }}>
        <h3>Import Results</h3>
        {importError && <div className="status error" style={{ marginBottom: 24 }}>{importError}</div>}
        {!importResult ? (
          <div className="status">No import run yet. After reviewing the preview, click Import to DB to save question tags and objective mappings.</div>
        ) : (
          <>
            <div className="summary-cards">
              <div className="summary-card"><div className="card-value">{importResult.questionCount}</div><div className="card-label">Questions Found</div></div>
              <div className="summary-card" style={{ background: '#eaffea' }}><div className="card-value" style={{ color: '#27ae60' }}>{importResult.importedCount}</div><div className="card-label">Tags Imported</div></div>
              <div className="summary-card"><div className="card-value">{importResult.updatedCount}</div><div className="card-label">Distinct Tags Upserted</div></div>
              <div className="summary-card"><div className="card-value" style={{ color: '#e67e22' }}>{importResult.skippedCount}</div><div className="card-label">Skipped (no tag)</div></div>
              <div className="summary-card"><div className="card-value" style={{ color: importResult.warningCount > 0 ? '#e67e22' : '#1976d2' }}>{importResult.warningCount || 0}</div><div className="card-label">Warnings</div></div>
            </div>
            <div className="status success">Import complete. Questions upserted into DB.</div>
            {importResult.warnings?.length > 0 && (
              <div className="warning">⚠ {importResult.warnings[0].questionName}: {importResult.warnings[0].warning}</div>
            )}
          </>
        )}
      </section>

      {/* Unmapped Incorrect Items */}
      <section style={{ marginBottom: 32 }}>
        <h3>Unmapped Incorrect Items</h3>
        <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
              <input
                placeholder="Course ID"
                value={unmappedFilter.courseId}
                onChange={(e) => setUnmappedFilter((current) => ({ ...current, courseId: e.target.value }))}
                style={{ width: 120, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4 }}
              />
              <input
                placeholder="Group ID (optional)"
                value={unmappedFilter.groupId}
                onChange={(e) => setUnmappedFilter((current) => ({ ...current, groupId: e.target.value }))}
                style={{ width: 160, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4 }}
              />
              <input
                placeholder="Group name (label only)"
                value={unmappedFilter.groupName}
                onChange={(e) => setUnmappedFilter((current) => ({ ...current, groupName: e.target.value }))}
                style={{ width: 260, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4 }}
              />
              <button onClick={() => handleLoadUnmappedItems()} disabled={unmappedLoading}>
                {unmappedLoading ? 'Loading...' : 'Load Unmapped Items'}
              </button>
            </div>

            {unmappedError && <div className="status error">{unmappedError}</div>}
            {unmappedNotice && <div className="status success">{unmappedNotice}</div>}

            {unmappedData && (
              <>
                <div className="summary-cards" style={{ marginBottom: 12 }}>
                  <div className="summary-card">
                    <div className="card-value">{unmappedData.totalUnmappedItems || 0}</div>
                    <div className="card-label">Unmapped Incorrect Items</div>
                  </div>
                  <div className="summary-card">
                    <div className="card-value">{unmappedData.studentsWithUnmapped?.length || 0}</div>
                    <div className="card-label">Students Affected</div>
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <button
                    onClick={handleApplyAllUnmappedOverrides}
                    disabled={unmappedLoading || unmappedBulkApplying}
                    style={{ background: '#2e7d32' }}
                  >
                    {unmappedBulkApplying ? 'Applying All...' : 'Apply All Entered Overrides'}
                  </button>
                </div>

                {(unmappedData.items || []).length === 0 ? (
                  <div className="status success">No unmapped incorrect items found for this course/group.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="unmapped-table">
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Score</th>
                          <th>Module</th>
                          <th>Question ID</th>
                          <th>Slot</th>
                          <th>Question Name</th>
                          <th>Type</th>
                          <th>State</th>
                          <th>Suggested Objective</th>
                          <th>Feedback Snippet</th>
                          <th>Assign Objective</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unmappedData.items.map((item, index) => {
                          const rowKey = getUnmappedRowKey(item, index);
                          return (
                          <tr key={`${item.studentId}-${item.questionId || item.slot || index}`}>
                            <td>{item.studentName}</td>
                            <td>{item.score?.toFixed ? item.score.toFixed(2) : item.score}</td>
                            <td>{item.moduleNum || '-'}</td>
                            <td>{item.questionId || '-'}</td>
                            <td>{item.slot || '-'}</td>
                            <td className="unmapped-question-cell" title={item.questionName || ''}>{item.questionName || '-'}</td>
                            <td>{item.questionType || '-'}</td>
                            <td>{item.questionState || '-'}</td>
                            <td style={{ minWidth: 220 }}>
                              {item.suggestedObjectiveNumber ? (
                                <>
                                  <div style={{ fontWeight: 600 }}>{item.suggestedObjectiveNumber}</div>
                                  <div style={{ fontSize: '0.78rem', color: '#666' }}>{item.suggestedObjectiveText || 'Objective text not found'}</div>
                                  <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 4 }}>Source: {item.suggestionSource || 'n/a'}</div>
                                  <button
                                    onClick={() => setUnmappedOverrideDrafts((current) => ({
                                      ...current,
                                      [rowKey]: item.suggestedObjectiveNumber
                                    }))}
                                    style={{ background: '#8e44ad', padding: '6px 10px', fontSize: '0.78rem', marginTop: 6, width: '100%' }}
                                  >
                                    Use Suggested
                                  </button>
                                </>
                              ) : (
                                <span style={{ color: '#999' }}>No suggestion</span>
                              )}
                            </td>
                            <td className="unmapped-feedback-cell">{item.feedbackSnippet || '-'}</td>
                            <td style={{ minWidth: 170 }}>
                              <input
                                value={unmappedOverrideDrafts[rowKey] || ''}
                                onChange={(e) => setUnmappedOverrideDrafts((current) => ({ ...current, [rowKey]: e.target.value }))}
                                placeholder="e.g. 5.2.10"
                                style={{ width: '100%', boxSizing: 'border-box', marginBottom: 6, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4 }}
                              />
                              <button
                                onClick={() => handleApplyUnmappedOverride(item, rowKey)}
                                disabled={unmappedSavingKey === rowKey || !item.moduleNum || !item.slot}
                                style={{ background: '#1976d2', padding: '6px 10px', fontSize: '0.8rem', width: '100%' }}
                              >
                                {unmappedSavingKey === rowKey ? 'Applying...' : 'Apply'}
                              </button>
                            </td>
                          </tr>
                        );})}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
        </>
      </section>

      {/* Current DB Mappings */}
      <section>
        <h3>Current DB Mappings</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
          <input placeholder="Module #" value={dbFilter.module} onChange={e => setDbFilter(f => ({ ...f, module: e.target.value }))} style={{ width: 80, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4 }} />
          <input placeholder="Tag (e.g. K-5.1.1)" value={dbFilter.tag} onChange={e => setDbFilter(f => ({ ...f, tag: e.target.value }))} style={{ width: 150, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4 }} />
          <input placeholder="Question name" value={dbFilter.questionName} onChange={e => setDbFilter(f => ({ ...f, questionName: e.target.value }))} style={{ width: 180, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4 }} />
          <button onClick={handleLoadDbMappings} disabled={dbLoading}>{dbLoading ? 'Loading...' : 'Load Mappings'}</button>
        </div>
        {dbError && <div className="status error">{dbError}</div>}
        {dbMappings && (
          dbMappings.length === 0
            ? <div className="status">No mappings found. Import an XML file first.</div>
            : (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ marginBottom: 8, color: '#555', fontSize: '0.9rem' }}>{dbMappings.length} record(s)</div>
                <table>
                  <thead>
                    <tr>
                      <th className="fit-col">ID</th>
                      <th>Question Name</th>
                      <th className="fit-col">Module</th>
                      <th className="fit-col">Tags</th>
                      <th className="fit-col objective-number-col">Objective Number</th>
                      <th>Objective Text</th>
                      <th>Suggested Fix</th>
                      <th>Override</th>
                      <th>Warning</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dbMappings.map((row) => (
                      <tr key={row.id}>
                        <td className="fit-col">{row.id}</td>
                        <td className="question-name-cell">{row.questionName}</td>
                        <td className="fit-col">{row.moduleNum}</td>
                        <td className="fit-col">{(row.tags || []).join(', ')}</td>
                        <td className="fit-col objective-number-value">{row.objectiveNumber || <span style={{ color: '#aaa' }}>none</span>}</td>
                        <td className="objective-text-cell" style={{ fontWeight: 600 }}>{row.objectiveText || <span style={{ color: '#aaa' }}>unmapped</span>}</td>
                        <td className="action-cell suggested-fix-cell">
                          {row.suggestedObjectiveNumber ? (
                            <>
                              <div style={{ fontWeight: 600 }}>{row.suggestedObjectiveNumber}</div>
                              <div style={{ fontSize: '0.78rem', color: '#666', marginBottom: 6 }}>{row.suggestedObjectiveText || 'Unknown objective'}</div>
                              <button
                                onClick={() => handleApplyOverride(row, row.suggestedObjectiveNumber)}
                                disabled={overrideSavingId === row.id || row.objectiveNumber === row.suggestedObjectiveNumber}
                                style={{ background: '#8e44ad', padding: '6px 10px', fontSize: '0.8rem' }}
                              >
                                Use Suggested
                              </button>
                            </>
                          ) : (
                            <span style={{ color: '#999' }}>No suggestion</span>
                          )}
                        </td>
                        <td className="action-cell override-cell">
                          <input
                            value={overrideDrafts[row.id] ?? row.overrideObjectiveNumber ?? ''}
                            onChange={(e) => setOverrideDrafts((current) => ({ ...current, [row.id]: e.target.value }))}
                            placeholder="e.g. 5.5.1"
                            style={{ width: '100%', boxSizing: 'border-box', marginBottom: 6, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4 }}
                          />
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleApplyOverride(row, overrideDrafts[row.id] ?? row.overrideObjectiveNumber ?? '')}
                              disabled={overrideSavingId === row.id}
                              style={{ background: '#1976d2', padding: '6px 10px', fontSize: '0.8rem' }}
                            >
                              Apply
                            </button>
                            {row.hasOverride && (
                              <button
                                onClick={() => handleClearOverride(row)}
                                disabled={overrideSavingId === row.id}
                                style={{ background: '#7f8c8d', padding: '6px 10px', fontSize: '0.8rem' }}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </td>
                        <td style={{ fontSize: '0.78rem', color: row.warning ? '#c0392b' : '#888' }}>{row.warning || ''}</td>
                        <td style={{ fontSize: '0.78rem', color: '#666' }}>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
        )}
      </section>
    </div>
  );
}
